<template>
  <ion-page>
    <PageHeaderWithMenu title="Релизы" :scrolled="headerScrolled" />
    <ion-content :class="[isMobile ? 'main-background' : 'liga-web-background']" :fullscreen="true" @ionScroll="onContentScroll">
      
      <!-- Add New Program Button -->
      <ion-card class="admin-card">
        <ion-card-content>
          <ion-button expand="block" @click="openNewProgramModal()">
            <ion-icon slot="start" :icon="add"></ion-icon>
            Создать новую программу
          </ion-button>
          <ion-button expand="block" @click="syncWithGoogleDrive()" :disabled="syncing">
            <ion-icon slot="start" :icon="cloudDownloadOutline"></ion-icon>
            {{ syncing ? 'Синхронизация...' : 'Синхронизировать с Google Drive' }}
          </ion-button>
        </ion-card-content>
      </ion-card>

      <!-- Programs list with releases -->
      <ion-card class="admin-card" v-for="program in programs" :key="program.id">
        <ion-card-header>
          <ion-card-title>{{ program.programName }}</ion-card-title>
          <p class="program-short-name">{{ program.shortName }}</p>
        </ion-card-header>
        <ion-card-content>
          <div class="program-logo-row">
            <div class="program-logo-wrapper">
              <img
                :src="logoDisplayUrl(program.shortName)"
                alt="Логотип программы"
                class="program-logo-img"
                @error="onLogoError(program.shortName)"
              />
            </div>
            <ion-button fill="clear" size="small" class="program-logo-edit-btn" @click="openLogoModal(program)">
              <ion-icon slot="start" :icon="imageOutline"></ion-icon>
              Изменить
            </ion-button>
          </div>
          <ion-button expand="block" @click="openNewReleaseModal(program)">
            <ion-icon slot="start" :icon="add"></ion-icon>
            Создать релиз
          </ion-button>
          
          <ion-list v-if="program.fullReleases && program.fullReleases.length">
            <ion-item v-for="release in program.fullReleases" :key="release.id" button @click="selectRelease(program, release)">
              <ion-label>
                <h2>{{ program.programName }} {{ release.releaseName }}</h2>
                <p>{{ release.visible ? 'Видимый' : 'Скрытый' }}</p>
              </ion-label>
              <ion-icon slot="end" :icon="chevronForward"></ion-icon>
            </ion-item>
          </ion-list>
          <p v-else class="empty-hint">Нет релизов для этой программы.</p>
        </ion-card-content>
      </ion-card>

      <!-- New Program Modal -->
      <ion-modal :is-open="showNewProgramModal" @didDismiss="closeNewProgramModal()" class="admin-releases-modal">
        <ion-header>
          <ion-toolbar>
            <ion-title>Новая программа</ion-title>
            <ion-buttons slot="end">
              <ion-button @click="closeNewProgramModal()">
                <ion-icon :icon="closeOutline"></ion-icon>
              </ion-button>
            </ion-buttons>
          </ion-toolbar>
        </ion-header>
        <ion-content>
          <ion-card>
            <ion-card-header>
              <ion-card-title>Создать программу</ion-card-title>
            </ion-card-header>
            <ion-card-content>
              <ion-item>
                <ion-input label="Название программы" label-placement="stacked" v-model="newProgramName" 
                  placeholder="Например: HypeDance"></ion-input>
              </ion-item>
              <ion-item>
                <ion-input label="Короткое название (латиницей)" label-placement="stacked" v-model="newProgramShortName" 
                  placeholder="Например: HD"></ion-input>
              </ion-item>
              <ion-button expand="block" @click="createNewProgram()" :disabled="!newProgramName || !newProgramShortName || creatingProgram">
                {{ creatingProgram ? 'Создание...' : 'Создать программу' }}
              </ion-button>
            </ion-card-content>
          </ion-card>
        </ion-content>
      </ion-modal>

      <!-- New Release Modal -->
      <ion-modal :is-open="showNewReleaseModal" @didDismiss="closeNewReleaseModal()" class="admin-releases-modal">
        <ion-header>
          <ion-toolbar>
            <ion-title>Новый релиз</ion-title>
            <ion-buttons slot="end">
              <ion-button @click="closeNewReleaseModal()">
                <ion-icon :icon="closeOutline"></ion-icon>
              </ion-button>
            </ion-buttons>
          </ion-toolbar>
        </ion-header>
        <ion-content>
          <ion-card v-if="selectedProgram">
            <ion-card-header>
              <ion-card-title>{{ selectedProgram.programName }}</ion-card-title>
            </ion-card-header>
            <ion-card-content>
              <ion-item>
                <ion-input label="Номер релиза" label-placement="stacked" v-model="newReleaseName" 
                  placeholder="Например: 1 или 10"></ion-input>
              </ion-item>
              <ion-item>
                <ion-input label="Описание (опционально)" label-placement="stacked" v-model="newReleaseDescription" 
                  placeholder="Описание релиза"></ion-input>
              </ion-item>
              <ion-item>
                <ion-toggle v-model="newReleaseVisible">Видимый для всех пользователей</ion-toggle>
              </ion-item>
              <ion-button expand="block" @click="createNewRelease()" :disabled="!newReleaseName || creating">
                {{ creating ? 'Создание...' : 'Создать' }}
              </ion-button>
            </ion-card-content>
          </ion-card>
        </ion-content>
      </ion-modal>

      <!-- Release Upload Modal -->
      <ion-modal :is-open="showUploadModal" @didDismiss="closeUploadModal()" class="admin-releases-modal">
        <ion-header>
          <ion-toolbar>
            <ion-title>Загрузка контента</ion-title>
            <ion-buttons slot="end">
              <ion-button @click="closeUploadModal()">
                <ion-icon :icon="closeOutline"></ion-icon>
              </ion-button>
            </ion-buttons>
          </ion-toolbar>
        </ion-header>
        <ion-content v-if="selectedRelease && selectedProgram">
          <ion-card>
            <ion-card-header>
              <ion-card-title>{{ selectedProgram.programName }} {{ selectedRelease.releaseName }}</ion-card-title>
            </ion-card-header>
            <ion-card-content>
              <!-- Music -->
              <ion-item>
                <ion-label>
                  <h2>Музыка</h2>
                  <p>Аудио файлы (конвертируются в MP3)</p>
                </ion-label>
              </ion-item>
              <ion-button expand="block" @click="selectFiles('music')">
                <ion-icon slot="start" :icon="musicalNotesOutline"></ion-icon>
                Загрузить музыку
              </ion-button>
              <input type="file" ref="musicInput" multiple accept="audio/*" style="display: none" @change="uploadFiles('music', $event)" />
              <div class="upload-modal-file-list">
                <ion-list v-if="releaseMediaFiles.music.length" class="transparent-list">
                  <ion-item v-for="f in releaseMediaFiles.music" :key="f.id" lines="full">
                    <ion-label>
                      <h3>{{ f.name }}</h3>
                      <p>{{ formatFileSize(f.weight) }}</p>
                    </ion-label>
                    <ion-button slot="end" fill="clear" color="danger" @click="confirmDeleteFile('music', f)">
                      <ion-icon :icon="trashOutline"></ion-icon>
                    </ion-button>
                  </ion-item>
                </ion-list>
                <p v-else-if="!releaseMediaLoading" class="empty-hint">Нет загруженных файлов</p>
              </div>

              <!-- Video -->
              <ion-item>
                <ion-label>
                  <h2>Видео</h2>
                  <p>Видео файлы (конвертируются для стриминга)</p>
                </ion-label>
              </ion-item>
              <ion-button expand="block" @click="selectFiles('video')">
                <ion-icon slot="start" :icon="videocamOutline"></ion-icon>
                Загрузить видео
              </ion-button>
              <input type="file" ref="videoInput" multiple accept="video/*" style="display: none" @change="uploadFiles('video', $event)" />
              <div class="upload-modal-file-list">
                <ion-list v-if="releaseMediaFiles.video.length" class="transparent-list">
                  <ion-item v-for="f in releaseMediaFiles.video" :key="f.id" lines="full">
                    <ion-label>
                      <h3>{{ f.name }}</h3>
                      <p>{{ formatFileSize(f.weight) }}</p>
                    </ion-label>
                    <ion-button slot="end" fill="clear" color="danger" @click="confirmDeleteFile('video', f)">
                      <ion-icon :icon="trashOutline"></ion-icon>
                    </ion-button>
                  </ion-item>
                </ion-list>
                <p v-else-if="!releaseMediaLoading" class="empty-hint">Нет загруженных файлов</p>
              </div>

              <!-- Guide (PDF) -->
              <ion-item>
                <ion-label>
                  <h2>Гайд (PDF)</h2>
                  <p>PDF файл (конвертируется в изображения)</p>
                </ion-label>
              </ion-item>
              <ion-button expand="block" @click="selectFiles('guide')">
                <ion-icon slot="start" :icon="documentTextOutline"></ion-icon>
                Загрузить гайд
              </ion-button>
              <input type="file" ref="guideInput" accept=".pdf,application/pdf" style="display: none" @change="uploadFiles('guide', $event)" />
              <div class="upload-modal-file-list">
                <ion-list v-if="releaseMediaFiles.guide.length" class="transparent-list">
                  <ion-item v-for="f in releaseMediaFiles.guide" :key="f.id" lines="full">
                    <ion-label>
                      <h3>{{ f.name }}</h3>
                      <p>{{ formatFileSize(f.weight) }}</p>
                    </ion-label>
                    <ion-button slot="end" fill="clear" color="danger" @click="confirmDeleteFile('guide', f)">
                      <ion-icon :icon="trashOutline"></ion-icon>
                    </ion-button>
                  </ion-item>
                </ion-list>
                <p v-else-if="!releaseMediaLoading" class="empty-hint">Нет загруженных PDF</p>
              </div>

              <!-- Cover -->
              <ion-item>
                <ion-label>
                  <h2>Обложка</h2>
                  <p>Изображение обложки релиза</p>
                </ion-label>
              </ion-item>
              <ion-button expand="block" @click="selectFiles('cover')">
                <ion-icon slot="start" :icon="imageOutline"></ion-icon>
                Загрузить обложку
              </ion-button>
              <input type="file" ref="coverInput" accept="image/*" style="display: none" @change="uploadFiles('cover', $event)" />
              <div class="upload-modal-file-list">
                <ion-list v-if="releaseMediaFiles.cover.length" class="transparent-list">
                  <ion-item v-for="f in releaseMediaFiles.cover" :key="f.id" lines="full">
                    <ion-label>
                      <h3>{{ f.name }}</h3>
                      <p>{{ formatFileSize(f.weight) }}</p>
                    </ion-label>
                    <ion-button slot="end" fill="clear" color="danger" @click="confirmDeleteFile('cover', f)">
                      <ion-icon :icon="trashOutline"></ion-icon>
                    </ion-button>
                  </ion-item>
                </ion-list>
                <p v-else-if="!releaseMediaLoading" class="empty-hint">Нет обложки</p>
              </div>

              <div v-if="uploadProgress.length > 0" class="upload-progress-list">
                <h3>Загрузка файлов</h3>
                <div v-for="(progress, idx) in uploadProgress" :key="'progress-' + idx" class="upload-progress-item">
                  <p>{{ progress.fileName }}</p>
                  <ion-progress-bar :value="progress.percent / 100"></ion-progress-bar>
                  <p class="progress-text">{{ progress.status }}</p>
                </div>
              </div>
              <ion-button expand="block" fill="outline" color="danger" class="ion-margin-top" @click="confirmDeleteRelease()">
                <ion-icon slot="start" :icon="trashOutline"></ion-icon>
                Удалить релиз
              </ion-button>
            </ion-card-content>
          </ion-card>
        </ion-content>
      </ion-modal>

      <!-- Program Logo Modal -->
      <ion-modal :is-open="showLogoModal" @didDismiss="closeLogoModal()" class="admin-releases-modal">
        <ion-header>
          <ion-toolbar>
            <ion-title>Логотип программы</ion-title>
            <ion-buttons slot="end">
              <ion-button @click="closeLogoModal()">
                <ion-icon :icon="closeOutline"></ion-icon>
              </ion-button>
            </ion-buttons>
          </ion-toolbar>
        </ion-header>
        <ion-content v-if="logoModalProgram">
          <ion-card class="admin-card">
            <ion-card-header>
              <ion-card-title>{{ logoModalProgram.programName }}</ion-card-title>
              <p class="program-short-name">{{ logoModalProgram.shortName }}</p>
            </ion-card-header>
            <ion-card-content>
              <div class="program-logo-row" v-if="logoDisplayUrl(logoModalProgram.shortName)">
                <div class="program-logo-wrapper program-logo-wrapper-preview">
                  <img
                    :src="logoDisplayUrl(logoModalProgram.shortName)"
                    alt="Логотип"
                    class="program-logo-img"
                    @error="onLogoError(logoModalProgram.shortName)"
                  />
                </div>
              </div>
              <p v-else class="empty-hint">Логотип не задан</p>
              <input
                type="file"
                ref="logoFileInput"
                accept="image/png,image/jpeg,image/jpg"
                style="display: none"
                @change="onLogoFileSelected($event)"
              />
              <ion-button expand="block" @click="triggerLogoFileInput()" :disabled="logoUploading">
                <ion-icon slot="start" :icon="imageOutline"></ion-icon>
                {{ logoUploading ? 'Загрузка...' : 'Выбрать файл (PNG, JPG)' }}
              </ion-button>
              <ion-button expand="block" fill="outline" color="danger" class="ion-margin-top"
                @click="removeProgramLogo()" :disabled="logoDeleting || !logoHasFile(logoModalProgram.shortName)">
                <ion-icon slot="start" :icon="trashOutline"></ion-icon>
                {{ logoDeleting ? 'Удаление...' : 'Удалить логотип' }}
              </ion-button>
            </ion-card-content>
          </ion-card>
        </ion-content>
      </ion-modal>

    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import { GlobalContext } from '@/model/globalcontext.model';
import { Program } from '@/model/program.model';
import { ProgramRelease } from '@/model/programrelease.model';
import PageHeaderWithMenu from '@/components/PageHeaderWithMenu.vue';
import {
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonPage,
  IonProgressBar,
  IonTitle,
  IonToggle,
  IonToolbar,
  alertController,
  toastController,
} from '@ionic/vue';
import { add, chevronForward, closeOutline, cloudDownloadOutline, documentTextOutline, imageOutline, musicalNotesOutline, trashOutline, videocamOutline } from 'ionicons/icons';
import { defineComponent, inject } from 'vue';
import axios from 'axios';

export default defineComponent({
  name: 'AdminReleases',
  components: {
    PageHeaderWithMenu,
    IonButton,
    IonButtons,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardTitle,
    IonContent,
    IonHeader,
    IonIcon,
    IonInput,
    IonItem,
    IonLabel,
    IonList,
    IonModal,
    IonPage,
    IonProgressBar,
    IonTitle,
    IonToggle,
    IonToolbar,
  },
  setup() {
    const globalContext = inject('globalContext') as GlobalContext;
    return {
      globalContext,
      isMobile: globalContext.isMobile,
      add,
      chevronForward,
      closeOutline,
      cloudDownloadOutline,
      documentTextOutline,
      imageOutline,
      musicalNotesOutline,
      trashOutline,
      videocamOutline,
    };
  },
  data() {
    return {
      headerScrolled: false,
      programs: [] as Program[],
      showNewProgramModal: false,
      showNewReleaseModal: false,
      showUploadModal: false,
      showLogoModal: false,
      logoModalProgram: null as Program | null,
      logoUploading: false,
      logoDeleting: false,
      logoLoadFailed: {} as Record<string, boolean>,
      logoExistsForModal: false,
      selectedProgram: null as Program | null,
      selectedRelease: null as ProgramRelease | null,
      newProgramName: '',
      newProgramShortName: '',
      newReleaseName: '',
      newReleaseDescription: '',
      newReleaseVisible: false,
      creatingProgram: false,
      creating: false,
      syncing: false,
      uploadProgress: [] as Array<{fileName: string, percent: number, status: string}>,
      releaseMediaFiles: {
        music: [] as Array<{ id: string; name: string; weight: number }>,
        video: [] as Array<{ id: string; name: string; weight: number }>,
        guide: [] as Array<{ id: string; name: string; weight: number }>,
        cover: [] as Array<{ id: string; name: string; weight: number }>,
      },
      releaseMediaLoading: false,
    };
  },
  computed: {
    apiBaseUrl(): string {
      return process.env.VUE_APP_API_URL || '';
    },
  },
  mounted() {
    this.loadPrograms();
  },
  methods: {
    onContentScroll(ev: any) {
      this.headerScrolled = ev.detail.scrollTop > 0;
    },
    
    async loadPrograms() {
      try {
        const response = await axios.get('/api/programs-with-releases');
        this.programs = response.data;
      } catch (error) {
        console.error('Failed to load programs:', error);
        const toast = await toastController.create({
          message: 'Не удалось загрузить список программ',
          duration: 3000,
          color: 'danger',
        });
        await toast.present();
      }
    },

    openNewProgramModal() {
      this.newProgramName = '';
      this.newProgramShortName = '';
      this.showNewProgramModal = true;
    },

    closeNewProgramModal() {
      this.showNewProgramModal = false;
      this.newProgramName = '';
      this.newProgramShortName = '';
    },

    async createNewProgram() {
      if (!this.newProgramName || !this.newProgramShortName) return;
      
      this.creatingProgram = true;
      try {
        await axios.post('/api/programs', {
          programName: this.newProgramName,
          shortName: this.newProgramShortName,
        });
        
        const toast = await toastController.create({
          message: 'Программа создана',
          duration: 2000,
          color: 'success',
        });
        await toast.present();
        
        this.closeNewProgramModal();
        this.loadPrograms();
      } catch (error) {
        console.error('Failed to create program:', error);
        const toast = await toastController.create({
          message: 'Ошибка создания программы',
          duration: 3000,
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.creatingProgram = false;
      }
    },

    async syncWithGoogleDrive() {
      this.syncing = true;
      try {
        const response = await axios.post('/api/admin/releases/sync-from-google');
        
        const toast = await toastController.create({
          message: `Синхронизация запущена. ${response.data.message || ''}`,
          duration: 5000,
          color: 'success',
        });
        await toast.present();
        
        // Reload programs after sync
        setTimeout(() => {
          this.loadPrograms();
        }, 2000);
      } catch (error) {
        console.error('Failed to sync with Google Drive:', error);
        const toast = await toastController.create({
          message: 'Ошибка синхронизации с Google Drive',
          duration: 3000,
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.syncing = false;
      }
    },

    openNewReleaseModal(program: Program) {
      this.selectedProgram = program;
      this.newReleaseName = '';
      this.newReleaseDescription = '';
      this.newReleaseVisible = false;
      this.showNewReleaseModal = true;
    },

    closeNewReleaseModal() {
      this.showNewReleaseModal = false;
      this.selectedProgram = null;
    },

    async createNewRelease() {
      if (!this.selectedProgram || !this.newReleaseName) return;
      
      this.creating = true;
      try {
        await axios.post('/api/releases', {
          releaseName: this.newReleaseName,
          releaseDescription: this.newReleaseDescription || null,
          visible: this.newReleaseVisible,
          programId: this.selectedProgram.id,
        });
        
        const toast = await toastController.create({
          message: 'Релиз создан',
          duration: 2000,
          color: 'success',
        });
        await toast.present();
        
        this.closeNewReleaseModal();
        this.loadPrograms();
      } catch (error) {
        console.error('Failed to create release:', error);
        const toast = await toastController.create({
          message: 'Ошибка создания релиза',
          duration: 3000,
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.creating = false;
      }
    },

    selectRelease(program: Program, release: ProgramRelease) {
      this.selectedProgram = program;
      this.selectedRelease = release;
      this.showUploadModal = true;
      this.uploadProgress = [];
      this.loadReleaseMediaLists();
    },

    logoDisplayUrl(shortName: string | undefined): string {
      const name = shortName ?? '';
      if (this.logoLoadFailed[name]) {
        return this.getProgramImage(name);
      }
      return `${this.apiBaseUrl}api/program-logos/${encodeURIComponent(name)}`;
    },

    onLogoError(shortName: string | undefined) {
      if (shortName != null) this.logoLoadFailed[shortName] = true;
    },

    getProgramImage(shortName: string | undefined): string {
      if (!shortName) return require('@/theme/liga-logo-white.png');
      try {
        return require(`@/theme/programs/${shortName}.png`);
      } catch {
        return require('@/theme/liga-logo-white.png');
      }
    },

    async openLogoModal(program: Program) {
      const shortName = program.shortName ?? '';
      if (!shortName) return;
      this.logoModalProgram = program;
      this.showLogoModal = true;
      this.logoExistsForModal = false;
      try {
        const res = await axios.get(`/api/admin/program-logos/${encodeURIComponent(shortName)}/exists`);
        this.logoExistsForModal = res.data === true;
      } catch {
        this.logoExistsForModal = false;
      }
    },

    closeLogoModal() {
      this.showLogoModal = false;
      this.logoModalProgram = null;
      this.logoUploading = false;
      this.logoDeleting = false;
    },

    logoHasFile(shortName: string | undefined): boolean {
      if (shortName == null) return false;
      return this.logoModalProgram?.shortName === shortName && this.logoExistsForModal;
    },

    triggerLogoFileInput() {
      const input = this.$refs.logoFileInput as HTMLInputElement | undefined;
      input?.click();
    },

    onLogoFileSelected(ev: Event) {
      const input = ev.target as HTMLInputElement;
      const file = input?.files?.[0];
      if (!file || !this.logoModalProgram) return;
      this.uploadLogo(file);
      input.value = '';
    },

    async uploadLogo(file: File) {
      const program = this.logoModalProgram;
      const shortName = program?.shortName ?? '';
      if (!program || !shortName) return;
      this.logoUploading = true;
      try {
        const formData = new FormData();
        formData.append('file', file);
        await axios.post(`/api/admin/program-logos/${encodeURIComponent(shortName)}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        this.logoExistsForModal = true;
        this.logoLoadFailed[shortName] = false;
        const toast = await toastController.create({
          message: 'Логотип загружен',
          duration: 2000,
          color: 'success',
        });
        await toast.present();
      } catch (error) {
        console.error('Failed to upload logo', error);
        const toast = await toastController.create({
          message: 'Ошибка загрузки логотипа',
          duration: 3000,
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.logoUploading = false;
      }
    },

    async removeProgramLogo() {
      const program = this.logoModalProgram;
      const shortName = program?.shortName ?? '';
      if (!program || !shortName) return;
      this.logoDeleting = true;
      try {
        await axios.delete(`/api/admin/program-logos/${encodeURIComponent(shortName)}`);
        this.logoExistsForModal = false;
        this.logoLoadFailed[shortName] = true;
        const toast = await toastController.create({
          message: 'Логотип удалён',
          duration: 2000,
          color: 'success',
        });
        await toast.present();
      } catch (error) {
        console.error('Failed to delete logo', error);
        const toast = await toastController.create({
          message: 'Ошибка удаления логотипа',
          duration: 3000,
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.logoDeleting = false;
      }
    },

    closeUploadModal() {
      this.showUploadModal = false;
      this.selectedRelease = null;
      this.selectedProgram = null;
      this.uploadProgress = [];
    },

    async loadReleaseMediaLists() {
      if (!this.selectedRelease) return;
      this.releaseMediaLoading = true;
      const id = this.selectedRelease.id;
      try {
        const [music, video, guide, cover] = await Promise.all([
          axios.get(`/api/releases/${id}/media/music`).then(r => r.data),
          axios.get(`/api/releases/${id}/media/video`).then(r => r.data),
          axios.get(`/api/releases/${id}/media/guide`).then(r => r.data),
          axios.get(`/api/releases/${id}/media/cover`).then(r => r.data),
        ]);
        this.releaseMediaFiles = { music, video, guide, cover };
      } catch (error) {
        console.error('Failed to load release media lists', error);
        const toast = await toastController.create({
          message: 'Не удалось загрузить списки файлов',
          duration: 3000,
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.releaseMediaLoading = false;
      }
    },

    formatFileSize(bytes: number): string {
      if (bytes < 1024) return `${bytes} Б`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
    },

    async confirmDeleteFile(subdir: 'music' | 'video' | 'guide' | 'cover', f: { id: string; name: string }) {
      const typeLabel = subdir === 'music' ? 'музыку' : subdir === 'video' ? 'видео' : subdir === 'guide' ? 'PDF' : 'обложку';
      const alert = await alertController.create({
        header: 'Удалить файл?',
        message: `Удалить ${typeLabel}: ${f.name}?${subdir === 'guide' ? ' Будут также удалены все сконвертированные страницы (изображения).' : ''}`,
        buttons: [
          { text: 'Отмена', role: 'cancel' },
          { text: 'Удалить', role: 'destructive', handler: () => this.deleteFile(subdir, f) },
        ],
      });
      await alert.present();
    },

    async deleteFile(subdir: 'music' | 'video' | 'guide' | 'cover', f: { id: string; name: string }) {
      if (!this.selectedRelease) return;
      try {
        await axios.delete(`/api/admin/releases/${this.selectedRelease.id}/file`, {
          params: { subdir, filename: f.name },
        });
        const toast = await toastController.create({
          message: `${f.name} удалён`,
          duration: 2000,
          color: 'success',
        });
        await toast.present();
        await this.loadReleaseMediaLists();
      } catch (error) {
        console.error('Delete file failed', error);
        const toast = await toastController.create({
          message: 'Ошибка удаления файла',
          duration: 3000,
          color: 'danger',
        });
        await toast.present();
      }
    },

    async confirmDeleteRelease() {
      if (!this.selectedRelease || !this.selectedProgram) return;
      const alert = await alertController.create({
        header: 'Удалить релиз?',
        message: `Релиз «${this.selectedProgram.programName} ${this.selectedRelease.releaseName}» будет удалён. Это действие нельзя отменить.`,
        buttons: [
          { text: 'Отмена', role: 'cancel' },
          { text: 'Удалить', role: 'destructive', handler: () => this.deleteRelease() },
        ],
      });
      await alert.present();
    },

    async deleteRelease() {
      if (!this.selectedRelease?.id) return;
      try {
        await axios.delete(`/api/releases/${this.selectedRelease.id}`);
        const toast = await toastController.create({
          message: 'Релиз удалён',
          duration: 2000,
          color: 'success',
        });
        await toast.present();
        this.closeUploadModal();
        this.loadPrograms();
      } catch (error) {
        console.error('Failed to delete release:', error);
        const toast = await toastController.create({
          message: 'Ошибка удаления релиза',
          duration: 3000,
          color: 'danger',
        });
        await toast.present();
      }
    },

    selectFiles(type: 'music' | 'video' | 'guide' | 'cover') {
      const input = this.$refs[type + 'Input'] as HTMLInputElement;
      if (input) input.click();
    },

    async uploadFiles(type: 'music' | 'video' | 'guide' | 'cover', event: Event) {
      if (!this.selectedRelease) return;
      
      const input = event.target as HTMLInputElement;
      const files = input.files;
      if (!files || files.length === 0) return;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        await this.uploadSingleFile(type, file);
      }
      
      // Clear input
      input.value = '';
    },

    async uploadSingleFile(type: string, file: File) {
      if (!this.selectedRelease) return;

      this.uploadProgress.push({
        fileName: file.name,
        percent: 0,
        status: 'Загрузка...',
      });
      const progressIndex = this.uploadProgress.length - 1;

      const formData = new FormData();
      formData.append('file', file);

      try {
        await axios.post(
          `/api/admin/releases/${this.selectedRelease.id}/upload/${type}`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (progressEvent) => {
              if (progressEvent.total && progressEvent.total > 0) {
                const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                const item = this.uploadProgress[progressIndex];
                if (item) {
                  item.percent = percent;
                  item.status = percent >= 100 ? 'Загружено' : 'Загрузка...';
                }
              }
            },
          }
        );

        const item = this.uploadProgress[progressIndex];
        if (item) {
          item.percent = 100;
          item.status = 'Загружено';
        }
        await this.loadReleaseMediaLists();
        const toast = await toastController.create({
          message: `${file.name} загружен`,
          duration: 2000,
          color: 'success',
        });
        await toast.present();
      } catch (error) {
        console.error('Upload failed:', error);
        const item = this.uploadProgress[progressIndex];
        if (item) item.status = 'Ошибка';
        const toast = await toastController.create({
          message: `Ошибка загрузки ${file.name}`,
          duration: 3000,
          color: 'danger',
        });
        await toast.present();
      }
    },
  },
});
</script>

<style scoped>
.admin-card {
  margin: 16px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  --color: rgba(255, 255, 255, 0.9);
}

.admin-card ion-card-title {
  color: white;
  font-weight: 600;
}

.program-short-name {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 4px;
}

.empty-hint {
  color: rgba(255, 255, 255, 0.6);
  font-style: italic;
  padding: 8px 0;
}

.program-logo-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

/* Логотипы обычно 3000×406 (широкий прямоугольник) */
.program-logo-wrapper {
  aspect-ratio: 3000 / 406;
  height: 40px;
  width: auto;
  max-width: 200px;
  border-radius: 6px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
}

.program-logo-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.program-logo-wrapper-preview {
  aspect-ratio: 3000 / 406;
  height: 80px;
  width: auto;
  max-width: 100%;
  min-width: 120px;
}

/* Убрать окантовку у кнопки «Изменить» */
.program-logo-edit-btn {
  --border-width: 0;
  --box-shadow: none;
}
.program-logo-edit-btn.ion-focused,
.program-logo-edit-btn:hover,
.program-logo-edit-btn.ion-activated {
  --border-width: 0;
  --box-shadow: none;
}

/* Прозрачные списки как в образовании */
.admin-card ion-list {
  background: transparent;
}

.admin-card ion-item {
  --background: transparent;
  --background-activated: transparent;
  --background-hover: transparent;
  --background-focused: transparent;
  --color: rgba(255, 255, 255, 0.9);
  --border-color: rgba(255, 255, 255, 0.15);
}

.admin-card ion-item::part(native) {
  background: transparent;
}

.admin-card ion-item ion-label h2 {
  color: white;
  font-weight: 500;
}

.admin-card ion-item ion-label p {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9em;
}

.admin-card ion-item ion-icon {
  color: var(--ion-color-orange);
}

/* Кнопки в стиле приложения: оранжевые */
.admin-card ion-button:not([fill="clear"]) {
  --background: var(--ion-color-orange);
  --background-activated: var(--ion-color-orange-tint);
  --background-hover: var(--ion-color-orange-tint);
  --color: white;
  --color-activated: white;
  font-weight: bold;
  --border-radius: 14px;
  margin-top: 8px;
}

.admin-card ion-button[fill="clear"] {
  --color: var(--ion-color-orange);
  --color-activated: var(--ion-color-orange-tint);
  font-weight: 600;
}

/* Прогресс загрузки */
.upload-progress-list {
  margin-top: 20px;
}

.upload-progress-item {
  margin-bottom: 12px;
  padding: 8px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
}

.upload-progress-item p {
  margin: 4px 0;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);
}

.progress-text {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.8rem;
}

.upload-progress-item ion-progress-bar {
  --progress-background: var(--ion-color-orange);
  --background: rgba(255, 255, 255, 0.1);
}
</style>

<style lang="css">
/* Модалки без scoped, т.к. контент в портале */
/* Модалка создания программы */
.admin-releases-modal ion-toolbar {
  --background: var(--ion-color-dark);
  --border-color: rgba(255, 255, 255, 0.15);
}

.admin-releases-modal ion-title {
  color: white;
  font-weight: 600;
}

.admin-releases-modal ion-content {
  --background: var(--ion-color-dark);
  --color: rgba(255, 255, 255, 0.9);
}

.admin-releases-modal ion-card {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  --color: rgba(255, 255, 255, 0.9);
}

.admin-releases-modal ion-card-title {
  color: white;
  font-weight: 600;
}

.admin-releases-modal ion-item {
  --background: transparent;
  --color: white;
  --border-color: rgba(255, 255, 255, 0.15);
}

.admin-releases-modal ion-label {
  color: rgba(255, 255, 255, 0.9);
}

.admin-releases-modal ion-input,
.admin-releases-modal ion-select {
  --color: white;
  --placeholder-color: rgba(255, 255, 255, 0.5);
}

.admin-releases-modal ion-toggle {
  --handle-background-checked: white;
  --background-checked: var(--ion-color-orange);
}

.admin-releases-modal ion-button:not([fill="clear"]) {
  --background: var(--ion-color-orange);
  --background-activated: var(--ion-color-orange-tint);
  --background-hover: var(--ion-color-orange-tint);
  --color: white;
  font-weight: bold;
  --border-radius: 14px;
  margin-top: 8px;
}

.admin-releases-modal ion-button[fill="clear"] {
  --color: var(--ion-color-orange);
  --color-activated: var(--ion-color-orange-tint);
  font-weight: 600;
}

/* Списки файлов в модалке загрузки — прозрачный фон как в медиатеке */
.upload-modal-file-list {
  margin-top: 8px;
  margin-bottom: 16px;
}
.admin-releases-modal .upload-modal-file-list .transparent-list {
  background: transparent;
}
.admin-releases-modal .upload-modal-file-list .transparent-list ion-item {
  --background: transparent;
  --background-activated: rgba(255, 255, 255, 0.06);
  --background-hover: rgba(255, 255, 255, 0.04);
  --background-focused: transparent;
  --border-color: rgba(255, 255, 255, 0.12);
  --color: rgba(255, 255, 255, 0.9);
}
.admin-releases-modal .upload-modal-file-list .transparent-list ion-item::part(native) {
  background: transparent;
}
.admin-releases-modal .upload-modal-file-list .transparent-list ion-item ion-label h3 {
  color: white;
  font-weight: 500;
}
.admin-releases-modal .upload-modal-file-list .transparent-list ion-item ion-label p {
  color: rgba(255, 255, 255, 0.65);
  font-size: 0.9em;
}
.admin-releases-modal .upload-modal-file-list .empty-hint {
  padding: 8px 0;
  color: rgba(255, 255, 255, 0.5);
  font-style: italic;
  font-size: 0.9rem;
}
</style>
